import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  type ElementBoundary,
  generateSnapThreshold,
  getSnapResultForTime,
} from '@src/utility/elementSnapping';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import { Draggable } from '../../Draggable';

interface SceneResizeHandleProps {
  side: 'start' | 'end';
  time: number;
  clamp: { min: number; max: number };
  snapBoundaries: ElementBoundary[];
  snapToFrame: (time: number) => number;
  disabled?: boolean;
  onPreview: (time: number) => void;
  onCommit: (time: number) => void;
  style?: React.CSSProperties;
}

export const SceneResizeHandle: React.FC<SceneResizeHandleProps> = observer(
  ({
    side,
    time,
    clamp,
    snapBoundaries,
    snapToFrame,
    disabled = false,
    onPreview,
    onCommit,
    style,
  }) => {
    const videoCreator = useVideoCreatorStore();
    const timelineScale = videoCreator.timelineScale;

    if (disabled || clamp.max - clamp.min <= 0) {
      return null;
    }

    const snapThreshold = generateSnapThreshold(timelineScale);

    return (
      <Draggable
        onStart={(e, data) => {
          e.preventDefault();
          return {
            startX: data.x,
            initialTime: time,
            lastTime: time,
          };
        }}
        onDrag={(e, data, context) => {
          e.preventDefault();
          if (!context) return;

          const delta = (data.x - context.startX) / timelineScale;
          let nextTime = clampTime(context.initialTime + delta, clamp);

          const snapResult = getSnapResultForTime(
            nextTime,
            snapBoundaries,
            snapThreshold,
          );

          if (snapResult.isWithinSnapWarningRange && snapResult.snapGuideTime) {
            videoCreator.setSnapGuide(
              true,
              snapResult.snapGuideTime,
              snapResult.boundary ?? undefined,
            );
          } else {
            videoCreator.setSnapGuide(false);
          }

          if (snapResult.isWithinSnapRange && snapResult.snapTime != null) {
            nextTime = clampTime(snapResult.snapTime, clamp);
          } else {
            nextTime = snapToFrame(nextTime);
          }

          context.lastTime = nextTime;
          onPreview(nextTime);
        }}
        onStop={(_, __, context) => {
          videoCreator.setSnapGuide(false);
          const nextTime = clampTime(context?.lastTime ?? time, clamp);
          onCommit(nextTime);
        }}
      >
        {ref => <Handle ref={ref} data-side={side} style={style} />}
      </Draggable>
    );
  },
);

function clampTime(value: number, clamp: { min: number; max: number }): number {
  return Math.min(Math.max(value, clamp.min), clamp.max);
}

const Handle = styled.div`
  position: absolute;
  cursor: ew-resize;
  z-index: 2;
  width: 8px;
  height: 100%;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: '';
    width: 2px;
    height: 18px;
    background: var(--foreground);
    border-radius: 1px;
    opacity: 0;
    transition: opacity var(--transition-fast) ease;
  }
  &:hover::before {
    opacity: 1;
  }
`;
